"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import  { useAppContext } from "../../contexts/AppContext"
import {
    Delete,
    ArrowUp,
    CornerDownLeft,
    Globe,
    Hash,
    Check
} from "lucide-react"

interface VirtualKeyboardProps {
    className?: string
    isVisible?: boolean
}

// Input types that don't support selection API
const NON_SELECTABLE_INPUT_TYPES = [
    'email',
    'number',
    'date',
    'time',
    'color',
    'range',
    'file',
    'month',
    'week',
    'datetime-local'
];

export function VirtualKeyboard({
                                    className = "",
                                    isVisible = true
                                }: VirtualKeyboardProps) {
    const [isShiftActive, setIsShiftActive] = useState(false)
    const [isCapsActive, setIsCapsActive] = useState(false)
    const [isSymbolsActive, setIsSymbolsActive] = useState(false)
    const { showKeyboard } = useAppContext()

    // Keep track of the last focused input element
    const lastFocusedRef = useRef<HTMLElement | null>(null)

    // Monitor focus changes to keep track of the focused element
    useEffect(() => {
        const handleFocus = (e: FocusEvent) => {
            if (e.target && (
                (e.target as HTMLElement).tagName === 'INPUT' ||
                (e.target as HTMLElement).tagName === 'TEXTAREA' ||
                (e.target as HTMLElement).getAttribute('contenteditable') === 'true'
            )) {
                lastFocusedRef.current = e.target as HTMLElement
            }
        }

        document.addEventListener('focusin', handleFocus)
        return () => {
            document.removeEventListener('focusin', handleFocus)
        }
    }, [])

    // Define keyboard rows - with standard and symbol layouts
    const standardRows = [
        ["`", "1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "-", "=", "backspace"],
        ["tab", "q", "w", "e", "r", "t", "y", "u", "i", "o", "p", "[", "]", "\\"],
        ["caps", "a", "s", "d", "f", "g", "h", "j", "k", "l", ";", "'", "< enter"],
        ["shift", "z", "x", "c", "v", "b", "n", "m", ",", ".", "/", ".ma", "@"],
        ["symbols", "space", "done"]
    ]

    const symbolRows = [
        ["~", "!", "@", "#", "$", "%", "^", "&", "*", "(", ")", "_", "+", "backspace"],
        ["tab", "€", "£", "¥", "₹", "₽", "₩", "₴", "₪", "{", "}", "|", "\\", "|"],
        ["caps", "©", "®", "™", "°", "•", "§", "¶", "†", "‡", "«", "»", "< enter"],
        ["shift", "¡", "¿", "÷", "×", "±", "≠", "≈", "<", ">", "?", ".ma", "@"],
        ["symbols", "space", "done"]
    ]

    // Get current layout based on mode
    const rows = isSymbolsActive ? symbolRows : standardRows

    // Helper to check if input supports selection
    const supportsSelection = (input: HTMLInputElement | HTMLTextAreaElement): boolean => {
        if (input.tagName === 'TEXTAREA') return true;
        if (input.tagName === 'INPUT') {
            const inputType = (input as HTMLInputElement).type.toLowerCase();
            return !NON_SELECTABLE_INPUT_TYPES.includes(inputType);
        }
        return false;
    }

    const triggerReactOnChange = (inputElement: HTMLInputElement | HTMLTextAreaElement, newValue: string) => {
        // Store original value
        const originalValue = inputElement.value;

        // This creates a tracker property that React can detect
        const tracker = Object.getOwnPropertyDescriptor(
            inputElement.constructor.prototype, 'value'
        );

        // Define our own property to trigger proper React change detection
        Object.defineProperty(
            inputElement, 'value',
            {
                configurable: true,
                get: function() { return originalValue; },
                set: function(val) {
                    // When React tries to set the value, we intercept
                    tracker?.set?.call(this, val);

                    // Trigger events React is listening for
                    const e = new Event('input', { bubbles: true });
                    this.dispatchEvent(e);
                }
            }
        );

        // Set the value to trigger our custom setter
        inputElement.value = newValue;

        // Remove our property to not mess with other operations
        Object.defineProperty(inputElement, 'value', tracker as PropertyDescriptor);

        // Manually trigger the input event
        const inputEvent = new Event('input', { bubbles: true });
        inputElement.dispatchEvent(inputEvent);

        // Also dispatch a change event after a short delay
        // This is important for libraries like Formik that rely on onChange
        setTimeout(() => {
            const changeEvent = new Event('change', { bubbles: true });
            inputElement.dispatchEvent(changeEvent);
        }, 0);
    };

    const handleKeyClick = (key: string) => {
        // Get the active input element
        const activeElement = lastFocusedRef.current || document.activeElement as HTMLElement

        // If there's no active element or it's not an input/textarea, we can't type into it
        if (!activeElement || (
            activeElement.tagName !== "INPUT" &&
            activeElement.tagName !== "TEXTAREA" &&
            activeElement.getAttribute('contenteditable') !== 'true'
        )) {
            console.log("No active input element found")
            return
        }

        // Make sure the element still has focus
        if (document.activeElement !== activeElement) {
            activeElement.focus()
        }

        // Handle special function keys
        if (key === "shift") {
            setIsShiftActive(!isShiftActive)
            return
        }

        if (key === "caps") {
            setIsCapsActive(!isCapsActive)
            return
        }

        if (key === "symbols") {
            setIsSymbolsActive(!isSymbolsActive)
            return
        }

        if (key === "space") {
            // Handle space key as a regular character
            key = " "
        }

        if (key === "done") {
            // Hide keyboard
            if (activeElement) {
                activeElement.blur();
            }
            return
        }

        // Process the key
        let processedKey = key

        // Apply shift or caps if active (but only for letters)
        if ((isShiftActive || isCapsActive) && key.length === 1 && key.match(/[a-z]/i)) {
            processedKey = key.toUpperCase()

            // Turn off shift after a key press (but not caps)
            if (isShiftActive) {
                setIsShiftActive(false)
            }
        }

        // Create appropriate key values for special keys
        let keyValue = processedKey
        let keyCode = ""

        switch (key) {
            case "backspace":
                keyValue = "Backspace"
                keyCode = "Backspace"
                break
            case "< enter":
                keyValue = "Enter"
                keyCode = "Enter"
                break
            case "tab":
                keyValue = "Tab"
                keyCode = "Tab"
                break
            case ".ma":
                keyValue = ".ma" // Ensure the ".ma" string is passed correctly
                keyCode = "Period"
                break
            default:
                keyCode = processedKey.length === 1
                    ? `Key${processedKey.toUpperCase()}`
                    : processedKey
        }

        // Special handling for direct input manipulation
        if (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA") {
            const inputElement = activeElement as HTMLInputElement | HTMLTextAreaElement

            // Check if this input type supports selection API
            const canUseSelection = supportsSelection(inputElement);

            // Get current value and position
            let newValue = inputElement.value;
            let newCursorPosition = canUseSelection ? (inputElement.selectionStart || 0) : 0;

            // For inputs that support selection, we get accurate cursor position
            let start = canUseSelection ? (inputElement.selectionStart || 0) : newValue.length;
            let end = canUseSelection ? (inputElement.selectionEnd || 0) : newValue.length;

            // Handle backspace key
            if (key === "backspace") {
                if (canUseSelection) {
                    if (start === end && start > 0) {
                        // Delete one character before cursor
                        newValue = inputElement.value.substring(0, start - 1) + inputElement.value.substring(end)
                        newCursorPosition = start - 1
                    } else if (start !== end) {
                        // Delete selected text
                        newValue = inputElement.value.substring(0, start) + inputElement.value.substring(end)
                        newCursorPosition = start
                    }
                } else {
                    // Simple deletion for non-selectable inputs (remove last character)
                    newValue = inputElement.value.substring(0, inputElement.value.length - 1)
                }
            }
            // Handle standard character input and special keys like .ma
            else if (keyValue.length >= 1 || key === "< enter" || key === "tab") {
                let insertChar = keyValue;

                // Special handling for .ma - ensure it inserts correctly
                if (key === ".ma") {
                    insertChar = ".ma";
                } else if (key === "< enter") {
                    insertChar = "\n";
                } else if (key === "tab") {
                    insertChar = "\t";
                }

                // Skip insertion if at max length (unless replacing selected text)
                if (
                    inputElement.maxLength > 0 &&
                    inputElement.value.length >= inputElement.maxLength &&
                    key !== "< enter" && key !== "tab" && key !== ".ma" &&
                    (!canUseSelection || start === end)
                ) {
                    return
                }

                if (canUseSelection) {
                    // Insert the character at cursor position
                    newValue = inputElement.value.substring(0, start) + insertChar + inputElement.value.substring(end)
                    newCursorPosition = start + insertChar.length
                } else {
                    // For inputs without selection, just append to the end
                    newValue = inputElement.value + insertChar
                }
            }

            // Use our specialized function to trigger React change detection
            triggerReactOnChange(inputElement, newValue);

            // Set the cursor position after React has updated, but only for selectable inputs
            if (canUseSelection) {
                setTimeout(() => {
                    try {
                        inputElement.selectionStart = newCursorPosition;
                        inputElement.selectionEnd = newCursorPosition;
                    } catch (e) {
                        // If we still get an error, just ignore
                        console.log("Selection API error:", e);
                    }
                }, 0);
            }

            // Special handling for OTP inputs
            if (/^[0-9]$/.test(keyValue) &&
                activeElement.tagName === 'INPUT' &&
                (activeElement as HTMLInputElement).maxLength === 1) {

                // This is likely a single-digit OTP input
                const otpInput = activeElement as HTMLInputElement

                // Try to find and focus the next input (common OTP pattern)
                const container = otpInput.parentElement?.parentElement

                if (container) {
                    // Find all input elements in this container
                    const allInputs = Array.from(container.querySelectorAll('input'))
                    const currentIndex = allInputs.indexOf(otpInput)

                    if (currentIndex >= 0 && currentIndex < allInputs.length - 1) {
                        // Focus the next input after React has processed the current change
                        setTimeout(() => {
                            allInputs[currentIndex + 1].focus()
                            // Update the last focused element
                            lastFocusedRef.current = allInputs[currentIndex + 1]
                        }, 10)
                    }
                }
            }
        } else if (activeElement.getAttribute('contenteditable') === 'true') {
            // Handle contenteditable elements
            if (key === "backspace") {
                document.execCommand('delete', false)
            } else if (key === "< enter") {
                document.execCommand('insertHTML', false, '<br>')
            } else if (key === ".ma") {
                document.execCommand('insertText', false, ".ma")
            } else if (keyValue.length === 1) {
                document.execCommand('insertText', false, keyValue)
            }
        }

        // Create universal keyboard events
        const keydownEvent = new KeyboardEvent("keydown", {
            key: keyValue,
            code: keyCode,
            keyCode: keyValue.length === 1 ? keyValue.charCodeAt(0) : 0,
            which: keyValue.length === 1 ? keyValue.charCodeAt(0) : 0,
            bubbles: true,
            cancelable: true,
            view: window,
            composed: true,
        })

        // Dispatch keypress for character keys
        if (keyValue.length === 1) {
            const keypressEvent = new KeyboardEvent("keypress", {
                key: keyValue,
                code: keyCode,
                keyCode: keyValue.charCodeAt(0),
                which: keyValue.charCodeAt(0),
                bubbles: true,
                cancelable: true,
                view: window,
                composed: true,
            })
            activeElement.dispatchEvent(keypressEvent)
        }

        // Dispatch keyup event
        const keyupEvent = new KeyboardEvent("keyup", {
            key: keyValue,
            code: keyCode,
            keyCode: keyValue.length === 1 ? keyValue.charCodeAt(0) : 0,
            which: keyValue.length === 1 ? keyValue.charCodeAt(0) : 0,
            bubbles: true,
            cancelable: true,
            view: window,
            composed: true,
        })

        // Dispatch events to the active element
        activeElement.dispatchEvent(keydownEvent)
        activeElement.dispatchEvent(keyupEvent)

        // Dispatch a custom event for specific listeners
        const customEvent = new CustomEvent("virtualKeyPress", {
            detail: {
                key: keyValue,
                originalKey: key,
                element: activeElement
            }
        })
        document.dispatchEvent(customEvent)
    }

    // Function to determine key display - now with icons
    const getKeyDisplay = (key: string) => {
        if (key === "backspace") return <Delete size={18} />
        if (key === "tab") return "Tab"
        if (key === "caps") return "Caps"
        if (key === "< enter") return <CornerDownLeft size={18} />
        if (key === "shift") return <ArrowUp size={18} />
        if (key === "symbols") return isSymbolsActive ? "ABC" : <Hash size={18} />
        if (key === "space") return "Space"
        if (key === "done") return <Check className="text-neutral-950 dark:text-neutral-50" size={18} />
        // Fix: Wrap adjacent JSX elements in a fragment
        if (key === ".ma") return (
            <div className="flex items-center">
                <Globe size={16} className="mr-1" />
                <span>.ma</span>
            </div>
        )

        // Apply shift or caps if active
        if ((isShiftActive || isCapsActive) && key.length === 1 && key.match(/[a-z]/i)) {
            return key.toUpperCase()
        }

        return key
    }

    // Function to determine key width class
    const getKeyWidthClass = (key: string) => {
        if (key === "backspace") return "w-[120px]"
        if (key === "tab") return "flex-1"
        if (key === "caps") return "flex-1"
        if (key === "< enter") return "flex-1"
        if (key === "shift") return "flex-1"
        if (key === "symbols") return "w-[100px]"
        if (key === "space") return "flex-1"
        if (key === "done") return "w-[100px]"
        if (key === ".ma") return "w-[70px]"
        return "w-[40px]"
    }

    return (
        <AnimatePresence>
            {showKeyboard && isVisible && (
                <motion.div
                    className={`fixed bottom-0 left-0 w-full virtual-keyboard ${className}`}
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                >
                    <div className="max-w-2xl mx-auto bg-white dark:bg-neutral-800 rounded-t-lg shadow-lg p-2">
                        {rows.map((row, rowIndex) => (
                            <div key={rowIndex} className="flex justify-center gap-1 mb-1">
                                {row.map((key, keyIndex) => {
                                    const isActive =
                                        (key === "shift" && isShiftActive) ||
                                        (key === "caps" && isCapsActive) ||
                                        (key === "symbols" && isSymbolsActive)

                                    return (
                                        <button
                                            key={`${rowIndex}-${keyIndex}`}
                                            className={`
                                            ${getKeyWidthClass(key)}
                                            h-12 rounded-md flex items-center justify-center
                                            border border-neutral-300 dark:border-neutral-600
                                            ${
                                                isActive
                                                    ? "bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200"
                                                    : "bg-white dark:bg-neutral-700 text-neutral-800 dark:text-white"
                                            }
                                            hover:bg-neutral-100 dark:hover:bg-neutral-600
                                            transition-colors shadow-sm
                                            ${key === ".ma" || key === "@" ? "text-emerald-600 dark:text-emerald-400 font-bold" : ""}
                                            ${key === "done" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                                            `}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                // Preserve the input focus when clicking a key
                                                if (lastFocusedRef.current) {
                                                    lastFocusedRef.current.focus();
                                                }
                                                // A small delay gives time for focus to be restored
                                                setTimeout(() => handleKeyClick(key), 0);
                                            }}
                                            onMouseDown={(e) => {
                                                // Prevent the default behavior that would take focus away
                                                e.preventDefault();
                                            }}
                                            type="button" // Prevent form submission
                                        >
                                            {getKeyDisplay(key)}
                                        </button>
                                    )
                                })}
                            </div>
                        ))}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
